import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Loader from "../../../components/Loader";

const inputClass =
  "border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full h-10";

const labelClass = "block text-xs font-semibold text-slate-700 mb-1";

const TransferStockModal = ({
  show,
  onClose,
  onTransfer,
  product,
  locations,
  loading,
}) => {
  const [selectedSubWId, setSelectedSubWId] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferQty, setTransferQty] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!show || !product) return null;

  const totalStock = product.warehouses?.reduce(
    (sum, w) => sum + Number(w.quantity || 0),
    0
  ) || 0;

  // Find the selected warehouse entry by its subdocument _id
  const selectedW = product.warehouses?.find(
    (w) => w._id?.toString() === selectedSubWId
  );

  const fromLocationId = selectedW
    ? (selectedW.location?._id || selectedW.location || selectedW.locationId)?.toString()
    : "";

  const availableQty = selectedW ? Number(selectedW.quantity || 0) : 0;
  const expiryDate = selectedW ? selectedW.expiryDate : null;
  const batchId = selectedW ? selectedW.batchId : "";

  const handleTransfer = () => {
    if (!selectedSubWId || !transferTo || !transferQty || Number(transferQty) <= 0) return;

    onTransfer({
      fromLocationId,
      toLocationId: transferTo,
      quantity: Number(transferQty),
      batchId,
      expiryDate,
    });
  };

  const isValid =
    selectedSubWId &&
    transferTo &&
    transferQty &&
    Number(transferQty) > 0 &&
    Number(transferQty) <= availableQty &&
    fromLocationId !== transferTo;

  // Sorted list of warehouses for the dropdown
  const sortedWarehouses = [...(product.warehouses || [])]
    .filter((w) => Number(w.quantity) > 0)
    .sort((a, b) => {
      const nameA = (a.location?.name || a.locationName || "Unknown").toLowerCase();
      const nameB = (b.location?.name || b.locationName || "Unknown").toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;

      const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : 0;
      const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : 0;
      return dateA - dateB;
    });

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-3">
      {/* Outer wrapper is overflow-hidden but tall enough to house dropdown */}
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl min-h-[500px] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        {/* HEADER */}
        <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
          <h2 className="text-lg font-semibold">Transfer Stock</h2>
          <button
            onClick={onClose}
            className="text-xl text-white hover:opacity-80 transition"
          >
            ✕
          </button>
        </div>

        {/* BODY (overflow-visible to let absolute elements render over footer) */}
        <div className="pb-4 space-y-4 flex-1 overflow-visible">
          {/* PRODUCT INFO */}
          <div className="flex gap-4 items-center bg-slate-50 border-b border-slate-200 p-4 px-6 rounded-none">
            {product.thumbnail ? (
              <img
                src={`${process.env.REACT_APP_IMAGE_URL}/${product.thumbnail}`}
                alt={product.name}
                className="w-16 h-16 rounded-lg object-contain bg-white border border-slate-200"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-lg border border-blue-200">
                {product.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Product</span>
              <h3 className="text-sm font-bold text-slate-800 truncate">{product.name}</h3>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {product.brand?.name && (
                  <span className="bg-blue-50 text-blue-700 text-xs px-2.5 py-0.5 rounded-full border border-blue-100 font-medium">
                    {product.brand.name}
                  </span>
                )}
                {product.category?.name && (
                  <span className="bg-purple-50 text-purple-700 text-xs px-2.5 py-0.5 rounded-full border border-purple-100 font-medium">
                    {product.category.name}
                  </span>
                )}
                <span className="bg-slate-100 text-slate-700 text-xs px-2.5 py-0.5 rounded-full font-medium border border-slate-200">
                  {product.quantityValue} {product.quantityUnit}
                </span>
                <span className="bg-green-50 text-green-700 text-xs px-2.5 py-0.5 rounded-full border border-green-100 font-medium">
                  Total Stock: {totalStock}
                </span>
              </div>
            </div>
          </div>

          {/* ROW 1: FROM WAREHOUSE (Floating Absolute Dropdown with z-[200] and max-h-64 for 6+ items) */}
          <div className="px-6 relative" ref={dropdownRef}>
            <label className={labelClass}>From Warehouse (Select Stock Batch)</label>
            
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="w-full h-10 border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition flex justify-between items-center text-slate-800 text-left shadow-sm font-sans"
            >
              {selectedW ? (
                <div className="flex justify-between items-center w-full">
                  <span className="font-bold text-[#172554]">
                    {selectedW.location?.name || selectedW.locationName || "Unknown Warehouse"}
                  </span>
                  <div className="flex gap-2 items-center">
                    {selectedW.batchId && selectedW.batchId !== "N/A" && (
                      <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-500 border border-slate-200">
                        Batch: {selectedW.batchId}
                      </span>
                    )}
                    <span className="text-xs text-slate-500 font-semibold">
                      {selectedW.expiryDate
                        ? `Exp: ${new Date(selectedW.expiryDate).toLocaleDateString()}`
                        : "No Expiry"}
                    </span>
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-bold border border-blue-100">
                      {selectedW.quantity} units
                    </span>
                  </div>
                </div>
              ) : (
                <span className="text-slate-400">Select source warehouse & batch...</span>
              )}
              <span className="text-slate-400 text-xs ml-2">▼</span>
            </button>

            {/* Custom dropdown list overlay (z-[200], absolute, max-h-64 holds ~6 options visible) */}
            {isOpen && (
              <div className="absolute left-6 right-6 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto z-[200] divide-y divide-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                {sortedWarehouses.length === 0 ? (
                  <div className="p-3 text-slate-400 text-xs text-center italic">
                    No available stock batches found.
                  </div>
                ) : (
                  (() => {
                    // Group options by warehouseId
                    const groups = {};
                    sortedWarehouses.forEach((w) => {
                      const locId = w.location?._id || w.location || "unknown";
                      const locName = w.location?.name || w.locationName || "Unknown Warehouse";
                      if (!groups[locId]) {
                        groups[locId] = {
                          name: locName,
                          items: [],
                          minExpiry: Infinity,
                        };
                      }
                      groups[locId].items.push(w);

                      const expTime = w.expiryDate ? new Date(w.expiryDate).getTime() : Infinity;
                      if (expTime < groups[locId].minExpiry) {
                        groups[locId].minExpiry = expTime;
                      }
                    });

                    // Sort groups by nearest expiry date first
                    const groupList = Object.values(groups).sort((a, b) => a.minExpiry - b.minExpiry);

                    // Sort items chronologically inside each group and calculate total combined stock
                    groupList.forEach((g) => {
                      g.items.sort((a, b) => {
                        const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
                        const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
                        return dateA - dateB;
                      });
                      g.totalQuantity = g.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
                    });

                    return groupList.map((group) => (
                      <div key={group.name} className="flex flex-col">
                        {/* Group Header Banner */}
                        <div className="bg-slate-50 px-4 py-1.5 font-extrabold text-[10px] text-[#172554] uppercase tracking-wider select-none border-b border-slate-200 flex justify-between items-center">
                          <span>{group.name}</span>
                          <span className="bg-[#172554] text-white px-2 py-0.5 rounded text-[9px] font-black shrink-0">
                            {group.totalQuantity} Total
                          </span>
                        </div>

                        {/* Items inside this group */}
                        <div className="divide-y divide-slate-50">
                          {group.items.map((w) => {
                            let expStr = "No Expiry Date";
                            if (w.expiryDate) {
                              try {
                                const d = new Date(w.expiryDate);
                                if (!isNaN(d.getTime())) {
                                  expStr = `Exp: ${d.toLocaleDateString()}`;
                                }
                              } catch (err) {}
                            }

                            const batchStr = w.batchId && w.batchId !== "N/A" ? w.batchId : "";

                            return (
                              <div
                                key={w._id}
                                onClick={() => {
                                  setSelectedSubWId(w._id);
                                  setIsOpen(false);
                                }}
                                className="flex justify-between items-center px-5 py-2 hover:bg-slate-50/50 cursor-pointer text-xs transition-colors"
                              >
                                <div className="flex flex-col gap-0.5">
                                  <span className={`font-bold ${w.expiryDate ? "text-slate-700" : "text-slate-500"}`}>
                                    {expStr}
                                  </span>
                                  {batchStr && (
                                    <span className="text-[9px] font-mono text-slate-400 leading-none">
                                      Batch: {batchStr}
                                    </span>
                                  )}
                                </div>
                                <span className="bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md font-bold border border-blue-100 text-xs shadow-sm">
                                  {w.quantity} units
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()
                )}
              </div>
            )}
          </div>

          {/* ROW 2: TO WAREHOUSE & QUANTITY */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6">
            <div>
              <label className={labelClass}>To Warehouse</label>
              <select
                className={inputClass}
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
              >
                <option value="">Select destination warehouse...</option>
                {locations
                  ?.filter((l) => l._id !== fromLocationId)
                  .map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Transfer Quantity {selectedSubWId && `(Max: ${availableQty})`}</label>
              <input
                type="number"
                min="1"
                max={availableQty}
                value={transferQty}
                onChange={(e) => setTransferQty(e.target.value)}
                className={inputClass}
                placeholder={selectedSubWId ? `Enter quantity (1-${availableQty})` : "Qty"}
                disabled={!selectedSubWId}
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t bg-white flex justify-end gap-3 z-10">
          <button
            onClick={onClose}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>

          <button
            onClick={handleTransfer}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
            disabled={loading || !isValid}
          >
            {loading ? (
              <Loader text="Transferring...." inline />
            ) : (
              "Transfer"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransferStockModal;
